import { booleanAttribute, Component, computed, input } from '@angular/core';
import { LoaderColors, LoaderSize } from '../loaders.models';

@Component({
  selector: 'mib-progress-bar',
  templateUrl: './progress-bar.component.html',
})
export class MibProgressBarComponent {
  /** The share done, from 0 to 100; `null`, `undefined` or a negative value draws an indeterminate bar. */
  readonly progress = input<number | null | undefined>(0);

  /** Thickness of the bar: `sm` 2, `md` 4, `lg` 6, `xl` 8px. */
  readonly size = input<LoaderSize>('md');

  /** The colour of the fill. */
  readonly color = input<LoaderColors>('primary');

  /** Writes the value as a percentage beside the bar, when it is a number. */
  readonly showPercent = input(false, { transform: booleanAttribute });

  /** What a screen reader says the bar reports. */
  readonly label = input('Loading');

  /** The height class of the bar for the current `size`. */
  readonly heightClasses = computed(() => {
    switch (this.size()) {
      case 'sm':
        return 'h-0.5';
      case 'lg':
        // eslint-disable-next-line mib/no-half-step-size -- the bar's 6px thickness, not a control height
        return 'h-1.5';
      case 'xl':
        return 'h-2';
      case 'md':
      default:
        return 'h-1';
    }
  });

  /** The fill colour class of the current `color`. */
  readonly colorClass = computed(() => {
    switch (this.color()) {
      case 'primary':
        return 'bg-mib-primary';
      case 'secondary':
        return 'bg-gray-700';
      case 'danger':
        return 'bg-mib-danger';
      case 'warning':
        return 'bg-mib-warning';
      case 'success':
        return 'bg-mib-success';
      case 'light':
        return 'bg-gray-200';
    }
  });

  /** The value held between 0 and 100, as the fill is drawn. */
  readonly clampedProgress = computed(() => {
    const value = this.progress();
    return value != null && value >= 0 ? Math.min(Math.max(value, 0), 100) : 0;
  });

  /** Whether the progress bar should be indeterminate */
  readonly isIndeterminate = computed(() => {
    const value = this.progress();
    return value == null || value < 0;
  });
}
